/**
 * HOOK: useContactFormSubmissions
 * Charge les form_submissions d'un contact avec les labels des champs.
 *
 * V5 R12 — chaque entrée `fields[]` expose maintenant `field_id`
 * (form_fields.id) et `options` (override + template) pour permettre
 * l'édition inline depuis FormSubmissionsSection.
 */

import { useCallback, useEffect, useState } from 'react';
import type { FormFieldType } from '../types/form';
import { useSupabase } from './useSupabase';

interface SubmissionField {
  /** UUID form_fields.id — clé utilisée pour patcher form_submissions.data */
  field_id: string;
  label: string;
  value: unknown;
  field_type: FormFieldType;
  /** Options (override ou template) — utiles pour <select> / <radio> en édition */
  options?: string[];
}

export interface FormSubmissionWithFields {
  id: string;
  form_id: string;
  form_name: string;
  bpi_code?: string;
  created_at: string;
  fields: SubmissionField[];
  /** Snapshot complet du JSONB data — utile pour éditer un champ "manquant" (vide) */
  raw_data: Record<string, unknown>;
}

export function useContactFormSubmissions(contactId: string | undefined) {
  const supabase = useSupabase();
  const [submissions, setSubmissions] = useState<FormSubmissionWithFields[]>([]);
  const [loading, setLoading] = useState(false);

  const loadSubmissions = useCallback(async () => {
    if (!contactId) {
      setSubmissions([]);
      return;
    }

    setLoading(true);

    try {
      // Charger les form_submissions avec form et form_fields (template + overrides)
      const { data, error } = await supabase
        .from('form_submissions')
        .select(
          `
          id, form_id, data, bpi_code, created_at,
          form:forms(
            id, name,
            fields:form_fields(
              id,
              label,
              options,
              field_template:field_templates(id, label, field_type, options)
            )
          )
        `
        )
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error('Error loading form submissions:', error);
        setSubmissions([]);
        return;
      }

      const transformed: FormSubmissionWithFields[] = (data || []).map((sub: any) => {
        const formFields: any[] = sub.form?.fields ?? [];
        const rawData: Record<string, unknown> = sub.data ?? {};

        // Pour V5 R12 on liste TOUS les champs du formulaire, pas seulement
        // ceux présents dans data — l'édition doit pouvoir cocher une case
        // "abonné newsletter" qui n'a jamais été cochée (donc absente du JSONB).
        const fields: SubmissionField[] = formFields.map((field) => {
          const tpl = field.field_template;
          return {
            field_id: field.id,
            label: field.label ?? tpl?.label ?? field.id,
            value: rawData[field.id],
            field_type: (tpl?.field_type ?? 'text') as FormFieldType,
            options: field.options ?? tpl?.options ?? undefined,
          };
        });

        return {
          id: sub.id,
          form_id: sub.form_id,
          form_name: sub.form?.name || 'Formulaire inconnu',
          bpi_code: sub.bpi_code,
          created_at: sub.created_at,
          fields,
          raw_data: rawData,
        };
      });

      setSubmissions(transformed);
    } catch (err) {
      console.error('Error in useContactFormSubmissions:', err);
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  }, [contactId, supabase]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  return { submissions, loading, refresh: loadSubmissions };
}
